import React, { useState, useMemo, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Dropdown } from 'primereact/dropdown';
import CSServiceUtil from '../../util/CSServiceUtil';

import { FILTER_OPTIONS, CHAT_TYPES } from './chatConstants';
import {
    getAvatarLabel,
    getBadgeValue,
    shouldShowBadge,
    getChatInfo,
    getChatDisplayName
} from './chatUtils';

const gServiceUtil = CSServiceUtil.cfGetInstance();
const ChatItem = ({ pChat, pOnClick = () => {}, pIsSelected }) => {
    const lItemClasses =
        `flex align-items-center p-3 cursor-pointer border-bottom-1 surface-border transition-duration-150 
        transition-colors ${pIsSelected ? 'bg-primary scale-in' : 'hover:bg-primary bg-primary-light'}`;

    const lDisplayName = getChatDisplayName(pChat);

    return (
        <div className={lItemClasses} onClick={() => pOnClick(pChat)}>
            <div className="relative">
                <Avatar
                    image      = {pChat?.avatar}
                    label      = {getAvatarLabel(pChat)}
                    size       = "large"
                    style={{
                        width    : "24px",
                        height   : "24px",
                        fontSize : "0.7rem"
                    }}
                    shape      = {pChat?.type === CHAT_TYPES.GROUP ? 'square' : 'circle'}
                    className  = "mr-3 border-2 surface-700"
                />
            </div>

            <div className="flex-grow-1 overflow-hidden min-w-0">
                <div className="flex justify-content-between align-items-center mb-1">
                    <div className="flex align-items-center">
                        <span
                            className={`font-medium text-xs white-space-nowrap
                                        text-overflow-ellipsis overflow-hidden
                                        ${pIsSelected ? 'text-primary' : 'text-gray-900'}`}
                        >
                            {lDisplayName}
                        </span>
                    </div>

                    <div className={`text-xs white-space-nowrap ${pIsSelected ? 'text-200' : 'text-800'}`}>
                        {pChat?.time || ''}
                    </div>
                </div>

                <div className="flex justify-content-between align-items-center">
                    <span
                        className={`text-2xs white-space-nowrap text-overflow-ellipsis overflow-hidden
                                    ${pIsSelected ? 'text-200' : 'text-500'}`}
                    >
                        {pChat?.lastMessage || ''}
                    </span>

                    <Badge
                        value      = {getBadgeValue(pChat?.unread || 0)}
                        severity   = "primary"
                        className  = "ml-2 flex-shrink-0"
                        style      = {shouldShowBadge(pChat?.unread || 0)}
                        size       = "small"
                    />
                </div>

                <div className={`text-xs mt-1 ${pIsSelected ? 'text-300' : 'text-600'}`}>
                    {getChatInfo(pChat)}
                </div>
            </div>
        </div>
    );
};

const ChatList = ({ pSelectedChat, pOnSelectChat, pContacts = [], pGroups   = [], pProps = {} }) => {
    const [mGroupList, setGroupList] = useState([]);
    const [mDisplayField, setDisplayField] = useState([]);
    const [mSelectedGroup, setSelectedGroup] = useState(null);
    const [mDependentField, setDependentField] = useState('All');
    const [mGroupField, setGroupField] = useState(null);
    const [mSearchTerm, setSearchTerm] = useState('');
    const [mSubTitle, setSubTitle] = useState('');

    const lFilteredChats = useMemo(() => {
        const lContacts = Array.isArray(pContacts) ? pContacts : [];
        const lGroups   = Array.isArray(pGroups)   ? pGroups   : [];

        let lAllChats = [...lContacts, ...lGroups].filter(lChat => !!lChat);
        let lChats = null;
        if (mDependentField && mSelectedGroup && mGroupField) {
            if(mSelectedGroup[mGroupField] === 'All') {
                lChats = lAllChats;
            }
            else {
                lChats = lAllChats.filter(lCtc => lCtc[mDependentField] === mSelectedGroup[mGroupField]);
            }
        }

        if (mSearchTerm) {
            const lTerm = mSearchTerm.toLowerCase();
            lChats = lChats.filter(lChat => {
                const lName       = getChatDisplayName(lChat).toLowerCase();
                const lLastMsg    = (lChat.lastMessage || '').toLowerCase();
                return lName.includes(lTerm) || lLastMsg.includes(lTerm);
            });
        }

        return lChats;
    }, [pContacts, pGroups, mSearchTerm, mSelectedGroup]);

    useEffect(() => {
            const fnInitializeForm = () => {
                if(pProps.pFormItems) {
                    for(let lInd=0; lInd<pProps.pFormItems.length; lInd++) {
                        let lFormItem = pProps.pFormItems[lInd];
                        if(!lFormItem.parent_ref_id_ && (lFormItem.control_type_ === 'combo_box') && lFormItem.data_provider_source_){
                            setDisplayField(lFormItem.data_provider_display_field_)
                            gServiceUtil.cfGetDataFromDataProvider (lFormItem.data_provider_source_, 
                                (lDataProviderId, lCollection) => 
                                {
                                    if(lDataProviderId === lFormItem.data_provider_source_){
                                        if(lCollection.length > 0) {
                                            setSelectedGroup(lCollection[0]);
                                            setGroupList(lCollection);
                                            setGroupField(lFormItem.data_provider_id_);
                                        }
                                    }
                                }
                            );
                        }
                        else if((lFormItem.control_type_ === 'list') && lFormItem.dependent_field_) {
                            setDependentField(lFormItem.dependent_field_);
                        }
                        else if((lFormItem.display_field_ === '#SUB_TITLE_1#') && lFormItem.parent_ref_id_) {
                            setSubTitle(lFormItem.data_field_);
                        }
                    }
                }
            }
        
            fnInitializeForm();
        }, []
    );

    return (
        <div className="flex flex-column bg-primary border-right-1 chat-list-container"
        style={{ height: '100%', minHeight: 0 }}>
            <div className="p-3 border-bottom-1 bg-primary">
                <Dropdown
                    value       = { mSelectedGroup }
                    options     = { mGroupList }
                    optionLabel = { mDisplayField }
                    filter      = {true}
                    onChange    = {(pEvent) => setSelectedGroup(pEvent.value)}
                    className   = "w-full"
                    placeholder = "All Contacts"
                />

                <div className="p-input-icon-left w-full mb-2">
                    <i className="pi pi-search text-500" />
                    <InputText
                        placeholder = "Search chats..."
                        className   = "w-full"
                        value       = {mSearchTerm}
                        onChange    = {(pEvent) => setSearchTerm(pEvent.target.value)}
                    /> 
                </div>
            </div>

            <div
                className="chat-scroll-panel"
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto'
                }}
            >
                {(lFilteredChats && (lFilteredChats.length) > 0) ? (
                    lFilteredChats.map((lChat, lIndex) => {
                        const lKey =
                            lChat?.id != null && lChat?.type
                                ? `${lChat.type}-${lChat.id}`
                                : `chat-${lIndex}`;

                        return (
                            <ChatItem
                                key         = {lKey}
                                pChat       = {lChat}
                                pOnClick    = {pOnSelectChat}
                                pIsSelected = {
                                    pSelectedChat && (pSelectedChat === lChat)
                                }
                            />
                        );
                    })
                ) : (
                    <div className="flex flex-column align-items-center justify-content-center p-4 text-center no-chats-placeholder">
                        <i className = "pi pi-inbox text-5xl text-600 mb-3"></i>
                        <p className = "text-500 mb-1">No chats found</p>
                        <p className = "text-600 text-sm">Try adjusting your search or filter</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatList;
